/* ============================================================================
 * jobFilesData.js  —  Job Files module data layer
 *
 * Storage:
 *   - Files persist as JSON in `settings` table key `job_files`.
 *     Shape: { [jobId]: [JobFile, ...] } (newest first)
 *   - Binary content lives in Supabase Storage bucket `portal-photos`
 *     under paths:
 *       jobs/{jobId}/photos/{ts}-{rand}.{ext}
 *       jobs/{jobId}/docs/{ts}-{rand}.{ext}
 *
 * Sharing model:
 *   - "Internal" (default) — only contractor sees in /dashboard/job-files.
 *   - "Shared" — file URL is also pushed into the relevant portal's
 *      photo list (settings.client_portal_shared_photos for clients,
 *      settings.crew_portal_photos for crew). Toggling off removes
 *      the portal entry but leaves the original file intact.
 *
 * JobFile shape:
 *   {
 *     id, name, kind: 'photo'|'doc', mime, size, url, path,
 *     uploaded_at, tag, caption,
 *     visibility: 'internal'|'shared',
 *     shared_with: { client: bool, crew_ids: string[] },
 *     // for sync: track the per-portal entry id so we can untag/unshare cleanly
 *     portal_refs: { client?: { client_id, photo_id }, crew?: [{ crew_id, photo_id }] }
 *   }
 * ========================================================================== */

import { supabase } from '@/lib/supabaseClient';
import { fetchSettingsByKey, upsertSettingsByKey } from '@/lib/settingsData';
import { uploadPortalPhoto, deletePortalPhoto, isStorageUrl } from '@/lib/portalStorage';

export const JOB_FILES_KEY = 'job_files';

/* ----------------------------------------------------------------------------
 * Tags
 * ------------------------------------------------------------------------- */
export const JOB_FILE_TAGS = [
  'Before',
  'During',
  'After',
  'Inspection',
  'Permit',
  'Material',
];

/* ----------------------------------------------------------------------------
 * MIME helpers
 * ------------------------------------------------------------------------- */
const PHOTO_MIME_RX = /^image\//i;

const DOC_MIME_LABELS = {
  'application/pdf': 'PDF',
  'application/msword': 'Word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
  'application/vnd.ms-excel': 'Excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
  'application/vnd.ms-powerpoint': 'PowerPoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint',
  'text/plain': 'Text',
  'text/csv': 'CSV',
};

export const fileKind = (file) =>
  file && PHOTO_MIME_RX.test(file.type || '') ? 'photo' : 'doc';

export const docTypeLabel = (mime) =>
  DOC_MIME_LABELS[mime] || (mime ? mime.split('/').pop().toUpperCase() : 'File');

const genId = (prefix) => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `${prefix}-${crypto.randomUUID().split('-')[0].toUpperCase()}`;
  }
  return `${prefix}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
};

/* ----------------------------------------------------------------------------
 * Loaders
 * ------------------------------------------------------------------------- */
export const fetchAllJobFiles = async () =>
  (await fetchSettingsByKey(JOB_FILES_KEY, {})).value || {};

export const fetchJobFilesFor = async (jobId) => {
  const all = await fetchAllJobFiles();
  return all[jobId] || [];
};

/* ----------------------------------------------------------------------------
 * Persist helper
 * ------------------------------------------------------------------------- */
const persist = async (next) => {
  await upsertSettingsByKey(JOB_FILES_KEY, next);
  return next;
};

/* ----------------------------------------------------------------------------
 * Upload
 * ------------------------------------------------------------------------- */
export const uploadJobFile = async (
  { jobId, file, tag = '', caption = '', visibility = 'internal' },
  current = {}
) => {
  if (!jobId) throw new Error('Missing job id');
  if (!file) throw new Error('Missing file');

  const kind = fileKind(file);
  const subpath = kind === 'photo' ? 'photos' : 'docs';
  const upload = await uploadPortalPhoto(file, {
    scope: 'job',
    ownerId: jobId,
    subpath,
  });
  if (!upload.ok) {
    throw new Error(upload.error || 'storage_upload_failed');
  }

  const entry = {
    id: genId('JF'),
    name: file.name || `${kind}-${Date.now()}`,
    kind,
    mime: file.type || '',
    size: Number(file.size) || 0,
    url: upload.url,
    path: upload.path,
    uploaded_at: new Date().toISOString(),
    tag: tag && JOB_FILE_TAGS.includes(tag) ? tag : '',
    caption: caption || '',
    visibility: visibility === 'shared' ? 'shared' : 'internal',
    shared_with: { client: false, crew_ids: [] },
    portal_refs: {},
  };

  const next = {
    ...current,
    [jobId]: [entry, ...(current[jobId] || [])],
  };
  await persist(next);
  return { next, entry };
};

/* ----------------------------------------------------------------------------
 * Update — tag / caption
 * ------------------------------------------------------------------------- */
export const updateJobFileMeta = async (
  { jobId, fileId, tag, caption },
  current = {}
) => {
  if (!jobId || !fileId) throw new Error('Missing identifiers');
  const arr = current[jobId] || [];
  const idx = arr.findIndex((f) => f.id === fileId);
  if (idx === -1) throw new Error('File not found');
  const nextItem = { ...arr[idx] };
  if (tag !== undefined) nextItem.tag = JOB_FILE_TAGS.includes(tag) ? tag : '';
  if (caption !== undefined) nextItem.caption = caption || '';
  const updated = arr.slice();
  updated[idx] = nextItem;
  const next = { ...current, [jobId]: updated };
  await persist(next);
  return next;
};

/* ----------------------------------------------------------------------------
 * Delete
 * ------------------------------------------------------------------------- */
export const deleteJobFile = async ({ jobId, fileId }, current = {}) => {
  if (!jobId || !fileId) throw new Error('Missing identifiers');
  const arr = current[jobId] || [];
  const target = arr.find((f) => f.id === fileId);
  if (!target) return current;

  // Unshare from any portals first to avoid orphan refs.
  if (target.shared_with?.client && target.portal_refs?.client?.client_id) {
    await _removeFromClientPortal({
      clientId: target.portal_refs.client.client_id,
      photoId: target.portal_refs.client.photo_id,
    }).catch(() => {});
  }
  if (Array.isArray(target.portal_refs?.crew)) {
    for (const ref of target.portal_refs.crew) {
      await _removeFromCrewPortal({ crewId: ref.crew_id, photoId: ref.photo_id }).catch(() => {});
    }
  }

  // Remove storage object best-effort.
  if (target.url && isStorageUrl(target.url)) {
    deletePortalPhoto(target.url).catch(() => {});
  }
  if (target.path && !target.url) {
    deletePortalPhoto(target.path).catch(() => {});
  }

  const next = { ...current, [jobId]: arr.filter((f) => f.id !== fileId) };
  await persist(next);
  return next;
};

/* ----------------------------------------------------------------------------
 * Sharing — Client Portal
 * ------------------------------------------------------------------------- */
const CLIENT_PHOTOS_KEY = 'client_portal_shared_photos';
const CREW_PHOTOS_KEY = 'crew_portal_photos';

const _addToClientPortal = async ({ clientId, file }) => {
  const cur = (await fetchSettingsByKey(CLIENT_PHOTOS_KEY, {})).value || {};
  const photoId = genId('PHO');
  const photo = {
    id: photoId,
    url: file.url,
    caption: file.caption || '',
    shared_at: new Date().toISOString(),
    source: 'job_files',
    job_file_id: file.id,
  };
  const next = {
    ...cur,
    [clientId]: [photo, ...(cur[clientId] || [])],
  };
  await upsertSettingsByKey(CLIENT_PHOTOS_KEY, next);
  return photoId;
};

const _removeFromClientPortal = async ({ clientId, photoId }) => {
  const cur = (await fetchSettingsByKey(CLIENT_PHOTOS_KEY, {})).value || {};
  const arr = (cur[clientId] || []).filter((p) => p.id !== photoId);
  const next = { ...cur, [clientId]: arr };
  await upsertSettingsByKey(CLIENT_PHOTOS_KEY, next);
};

const _addToCrewPortal = async ({ crewId, file, jobId, jobName }) => {
  const cur = (await fetchSettingsByKey(CREW_PHOTOS_KEY, {})).value || {};
  const photoId = genId('CPHO');
  const photo = {
    id: photoId,
    url: file.url,
    job_id: jobId || null,
    job_name: jobName || '',
    caption: file.caption || '',
    uploaded_at: new Date().toISOString(),
    source: 'job_files',
    job_file_id: file.id,
  };
  const next = {
    ...cur,
    [crewId]: [photo, ...(cur[crewId] || [])],
  };
  await upsertSettingsByKey(CREW_PHOTOS_KEY, next);
  return photoId;
};

const _removeFromCrewPortal = async ({ crewId, photoId }) => {
  const cur = (await fetchSettingsByKey(CREW_PHOTOS_KEY, {})).value || {};
  const arr = (cur[crewId] || []).filter((p) => p.id !== photoId);
  const next = { ...cur, [crewId]: arr };
  await upsertSettingsByKey(CREW_PHOTOS_KEY, next);
};

/**
 * Toggle sharing of a job file with the client portal.
 * Pass `clientId` (the client this job belongs to) when enabling.
 */
export const setShareToClient = async (
  { jobId, fileId, enabled, clientId },
  current = {}
) => {
  const arr = current[jobId] || [];
  const idx = arr.findIndex((f) => f.id === fileId);
  if (idx === -1) throw new Error('File not found');
  const file = { ...arr[idx] };
  const refs = { ...(file.portal_refs || {}) };

  // Only photos can be pushed to the client portal photo gallery.
  // Documents are skipped silently.
  if (file.kind !== 'photo') return current;

  if (enabled) {
    if (!clientId) throw new Error('Missing client id');
    if (refs.client?.photo_id && refs.client?.client_id === clientId) {
      // already shared with this client; no-op
    } else {
      // remove old client reference if it points to a different client
      if (refs.client?.photo_id && refs.client?.client_id) {
        await _removeFromClientPortal({
          clientId: refs.client.client_id,
          photoId: refs.client.photo_id,
        }).catch(() => {});
      }
      const photoId = await _addToClientPortal({ clientId, file });
      refs.client = { client_id: clientId, photo_id: photoId };
    }
    file.shared_with = { ...(file.shared_with || {}), client: true };
    file.visibility = 'shared';
  } else {
    if (refs.client?.photo_id && refs.client?.client_id) {
      await _removeFromClientPortal({
        clientId: refs.client.client_id,
        photoId: refs.client.photo_id,
      }).catch(() => {});
    }
    delete refs.client;
    file.shared_with = { ...(file.shared_with || {}), client: false };
    if (!file.shared_with.crew_ids?.length) file.visibility = 'internal';
  }

  file.portal_refs = refs;
  const updated = arr.slice();
  updated[idx] = file;
  const next = { ...current, [jobId]: updated };
  await persist(next);
  return next;
};

/**
 * Toggle sharing of a job file with one or more crew members.
 * Pass `crewIds` array of crew member ids that should now have access.
 */
export const setShareToCrew = async (
  { jobId, fileId, crewIds = [], jobName = '' },
  current = {}
) => {
  const arr = current[jobId] || [];
  const idx = arr.findIndex((f) => f.id === fileId);
  if (idx === -1) throw new Error('File not found');
  const file = { ...arr[idx] };
  const refs = { ...(file.portal_refs || {}) };
  const currentCrewRefs = Array.isArray(refs.crew) ? refs.crew : [];
  const wantSet = new Set((crewIds || []).filter(Boolean));

  if (file.kind !== 'photo') return current;

  // Remove crew refs no longer wanted
  const keptRefs = [];
  for (const ref of currentCrewRefs) {
    if (wantSet.has(ref.crew_id)) {
      keptRefs.push(ref);
    } else {
      await _removeFromCrewPortal({ crewId: ref.crew_id, photoId: ref.photo_id }).catch(() => {});
    }
  }
  // Add newly requested crew refs
  const haveSet = new Set(keptRefs.map((r) => r.crew_id));
  for (const crewId of wantSet) {
    if (haveSet.has(crewId)) continue;
    const photoId = await _addToCrewPortal({ crewId, file, jobId, jobName });
    keptRefs.push({ crew_id: crewId, photo_id: photoId });
  }

  if (keptRefs.length > 0) {
    refs.crew = keptRefs;
  } else {
    delete refs.crew;
  }

  file.portal_refs = refs;
  file.shared_with = {
    ...(file.shared_with || {}),
    crew_ids: keptRefs.map((r) => r.crew_id),
  };
  if (file.shared_with.client || keptRefs.length > 0) {
    file.visibility = 'shared';
  } else {
    file.visibility = 'internal';
  }

  const updated = arr.slice();
  updated[idx] = file;
  const next = { ...current, [jobId]: updated };
  await persist(next);
  return next;
};

/* ----------------------------------------------------------------------------
 * Helpers for UI
 * ------------------------------------------------------------------------- */
export const formatBytes = (n) => {
  const num = Number(n) || 0;
  if (num < 1024) return `${num} B`;
  if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
  return `${(num / (1024 * 1024)).toFixed(1)} MB`;
};

export const splitByKind = (files = []) => {
  const photos = [];
  const docs = [];
  for (const f of files) {
    if (f.kind === 'photo') photos.push(f);
    else docs.push(f);
  }
  return { photos, docs };
};

/**
 * Bring the contractor-side jobs list (with id + name + client_id + client text)
 * needed by the picker.
 */
export const fetchJobsForFiles = async () => {
  const { data, error } = await supabase
    .from('jobs')
    .select('id,name,client,client_id,address,status,phase,created_at')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
};
